/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import uniq from 'lodash.uniq';
import {
  BulkActionEntity,
  CarbDevices,
  Devices,
  GenericObject,
  SentinelDevices
} from '@ion/core/src/types/entities';
import {
  DATE_RANGE_FORMAT,
  dateFormatter,
  isValid,
  resolveDateInput,
  xlsxDatetoISOString
} from '@ion/core/src/utils/DateUtil';
import {isValidESN} from '@ion/core/src/utils/DeviceUtils';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {ConflictResolutionOptions, excludeDevicesFromCriteria} from '../components/jobs/JobConflictUtils';

interface ShouldUseNoConflictsDevicesParams {
  areTotalRowsSelected?: boolean;
  isJobCreation: boolean;
  jobSearchEngineEnabled: boolean;
  noConflictsDevices?: string[] | null;
  identifierType: string;
}

export enum BulkSelectEntitiesFormFields {
  EntityIdentifierType = 'entityIdentifierType',
  EntityIdentifiers = 'entityIdentifiers',
  File = 'file'
}

export const MAXIMUM_ENTITY_COUNT = 5000;

export const LMU_DEVICE_TYPE = 'LMU2650';

export const VIN_REG_EXP = /^[A-HJ-NPR-Z0-9]{11,17}$/;

// Identifier types whose values match the devices kept after resolving job conflicts.
const NO_CONFLICTS_IDENTIFIER_TYPES = ['id', 'esn', 'serialNumber'];

// Builds the initial form state for the Bulk entity Action dialog.
export const getInitialBulkSelectEntitiesFormState = (): FormState => ({
  [BulkSelectEntitiesFormFields.EntityIdentifierType] : {
    name     : BulkSelectEntitiesFormFields.EntityIdentifierType,
    required : true
  },
  [BulkSelectEntitiesFormFields.EntityIdentifiers] : {
    name     : BulkSelectEntitiesFormFields.EntityIdentifiers,
    required : true
  },
  [BulkSelectEntitiesFormFields.File] : {
    name     : BulkSelectEntitiesFormFields.File,
    required : false,
    value    : {
      importParams : {
        items      : [],
        isComplete : false,
        isFailure  : false,
        workbook   : null
      },
      importComplete : false,
      importStatus   : false
    }
  }
});

// Serializes the form state into an API-consumable entity Config
export const serialize = (
  formState: FormState): GenericObject => {
  const serialized: GenericObject = {};
  for (const [key, {value}] of Object.entries(formState)) {
    if (value) {
      serialized[key] = value;
    }
  }
  return serialized;
};

/**
 * Determines if the given import params are for a valid file
 * @param importParams import details from uploading / importing a file
 * @returns true if import params contain a file, false otherwise
 */
export const isValidFile = (importParams) => importParams?.fileName ? importParams : false;

/**
 * Determines if the given ids are valid based on the provided entity type (device, asset, etc) & identifier type.
 * @param ids list of identifiers to validate
 * @param entity device, asset, etc
 * @returns true if valid, false otherwise
 */
export const arePastedIdsValid = (ids, entity: BulkActionEntity, validate = true) => {
  if (ids?.length) {
    if (entity === BulkActionEntity.DEVICES) {
      return validate ? !ids.some(id => !isValidESN(id)) : true;
    } else {
      return true;
    }
  }
  return true;
};

/**
 * Parses the specified string containing pasted identifiers (delimiters can be
 * whitespace or commas) and returns a list of the identifiers with any duplicates
 * removed. Note that any leading and trailing delimiters are removed during the
 * parsing process.
 * @param ids string containing identifiers to parse
 * @returns list of identifiers
 */
export const parsePastedIds = (ids: string): string[] => {
  if (ids?.length) {
    return uniq(ids.replace(/^[\s,]+|[\s,]+$/gm, '').split(/[\s,]+/));
  }
  return [];
};

/**
 * Returns array of objects with primary and secondary device identifiers based on default identifier and
 * devices provided.
 * 
 * @param devices Array of devices of Devices type or from uploaded file
 * @param defaultIdentifier string of device identifier
 * @returns Array of objects with primary and secondary device identifiers
 */
export const getPairedDevices = (
  devices: Devices | {deviceIdentifier: number | string, secondaryDeviceIdentifier: number | string}[] | string[] = [],
  defaultIdentifier: string = null,
  filterLmu: boolean = false): {pairedDevices: SentinelDevices, unallowedDevices?: Devices} => {
  let filteredDevices = devices;
  let unallowedDevices = [];
  if (filterLmu) {
    const selectedDevices = devices as Devices;
    filteredDevices = selectedDevices?.filter(({typeName, sentinelUI}) => typeName === LMU_DEVICE_TYPE && sentinelUI === '');
    unallowedDevices = selectedDevices?.filter(({typeName, sentinelUI}) => typeName !== LMU_DEVICE_TYPE || (typeName === LMU_DEVICE_TYPE && sentinelUI !== ''));
  }
  const pairedDevices = filteredDevices?.map(device => {
    return {
      primaryDevice : defaultIdentifier
        ? device[defaultIdentifier]?.toString() || device?.deviceIdentifier?.toString() : device?.toString(),
      secondaryDevice : defaultIdentifier ? device?.secondaryDeviceIdentifier?.toString() || '' : ''
    };
  });
  return filterLmu ? {pairedDevices, unallowedDevices} : {pairedDevices};
};

export const getCarbDevices = ({
  devices,
  defaultIdentifier = null
}: {devices: Devices | CarbDevices, defaultIdentifier: string}) => {
  return devices?.map(device => {
    const registrationRenewalDate = device?.['Registration Renewal Date (Optional)'];
    const normalizedDate = registrationRenewalDate
      ? xlsxDatetoISOString(registrationRenewalDate)
      : new Date().toISOString();

    return {
      identifier : defaultIdentifier
        ? device[defaultIdentifier]?.toString() || device?.deviceIdentifier?.toString() : device?.toString(),
      vin                     : device?.vin || device?.['VIN (Optional)'] || '',
      licensePlate            : device?.licensePlate || device?.['License Plate (Optional)'] || '',
      licenseState            : device?.licenseState || device?.['License State (Optional)'] || '',
      registrationRenewalDate : dateFormatter(normalizedDate, DATE_RANGE_FORMAT)
    };
  }) ?? [];
};

/**
 * Parses a stored `"YYYY-MM-DD"` registration renewal date into a local-time
 * `Date` for the CARB service form's date picker. A bare date-only string is
 * otherwise parsed by native `Date` as UTC midnight, which renders a day
 * earlier in any timezone behind UTC; `resolveDateInput` anchors it to local
 * midnight so the picker displays and submits the intended calendar day.
 *
 * ```typescript
 *
 *  import {toLocalRenewalDate} from './BulkSelectEntitiesEditorUtils';
 *  ...
 *
 *  const renewal = toLocalRenewalDate('2026-06-16'); // local Jun 16, not UTC
 *
 * ```
 *
 * @param value stored renewal date as a `"YYYY-MM-DD"` string
 * @returns a local-time `Date`, or `undefined` when the value is missing or invalid
 */
export const toLocalRenewalDate = (value?: string): Date | undefined => (
  value && isValid(value) ? resolveDateInput(value) : undefined
);

export const extractUniqueTaskIds = (selectedOpts: ConflictResolutionOptions) => {
  const taskIds = new Set();
  const result = [];

  Object.keys(selectedOpts).forEach(key => {
    const [id, _] = key.split('-');
    const step = selectedOpts[key].activeStep;
    const uniqueKey = `${id}-${step}`;

    if (!taskIds.has(uniqueKey)) {
      taskIds.add(uniqueKey);
      result.push(Number(id));
    }
  });

  return result;
};

/**
 * Returns `true` when a bulk job creation must be submitted with the devices left
 * after resolving its conflicts, excluding the ones resolved with "Remove Devices
 * from Current Job", otherwise `false`.
 *
 * @param areTotalRowsSelected   whether every row of the grid is selected
 * @param isJobCreation          whether the bulk action is a job creation
 * @param jobSearchEngineEnabled whether the job conflicts check is enabled
 * @param noConflictsDevices     identifiers of the devices left after resolving conflicts
 * @param identifierType         selected device identifier type
 * @returns `true` when the devices left after resolving conflicts must be used, otherwise `false`
 */
export const shouldUseNoConflictsDevices = ({
  areTotalRowsSelected,
  isJobCreation,
  jobSearchEngineEnabled,
  noConflictsDevices,
  identifierType
}: ShouldUseNoConflictsDevicesParams): boolean => (
  !areTotalRowsSelected && isJobCreation && jobSearchEngineEnabled && !!noConflictsDevices?.length &&
  NO_CONFLICTS_IDENTIFIER_TYPES.includes(identifierType)
);

/**
 * Excludes the devices resolved with "Remove Devices from Current Job" from a bulk
 * operation search criteria (`smartSearch.search`), used when every row of the grid
 * is selected.
 *
 * @param searchCriteria bulk operation search criteria
 * @param ids            device ids to exclude
 * @returns the search criteria excluding the specified devices
 */
export const excludeRemovedDevicesFromSearchCriteria = <T, >(searchCriteria: T, ids: number[]): T => {
  let result: T     = searchCriteria;
  const smartSearch = (searchCriteria as GenericObject)?.smartSearch;
  const search      = smartSearch?.search;
  if (ids?.length && search) {
    result = {
      ...searchCriteria,
      smartSearch : {
        ...smartSearch,
        search : excludeDevicesFromCriteria({smartSearch: search}, uniq(ids)).smartSearch
      }
    } as T;
  }
  return result;
};
